/* eslint-disable react/jsx-wrap-multilines */
import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';

import classNames from 'classnames';

import { Button, Container, Loader } from 'semantic-ui-react';

import '../css/Gradebook.less';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import { GRADEBOOK_CONTEXT, GRADEBOOK_TYPE, OBSERVATIONAL_GRADER_TOGGLE_STATUS } from '../constants';

import { ASSIGNMENT_STATUS, ASSIGNMENT_TYPE, PDF_DELIVERY_FORMATS } from '../managers/AssignmentManager';
import Auth from '../managers/AuthManager';
import { CONTENT_ITEM_TYPES, CONTENT_MODE, PLAYER_TYPES } from '../managers/ContentManager';
import { DIALOG_NAMES } from '../managers/DialogManager';
import { VIEW_SELECTION } from '../managers/NavigationManager';

import AssignmentService from '../services/AssignmentService';
import ClassroomService from '../services/ClassroomService';
import ContentService from '../services/ContentService';
import CourseService from '../services/CourseService';
import EngagementService from '../services/EngagementService';
import GradebookMainService from '../services/gradebook/GradebookMainService';
import ImageService from '../services/ImageService';
import PresentationOptionsService from '../services/PresentationOptionsService';
import ReportBreadcrumbService from '../services/reports/ReportBreadcrumbService';
import ResourcePacingService from '../services/ResourcePacingService';
import UtilityService from '../services/UtilityService';

export default
@inject(
  'assignmentManager', 'classroomManager', 'contentManager',
  'courseManager', 'dialogManager', 'engagementManager',
  'exportManager', 'gradebookManager', 'navigationManager',
  'productManager', 'userManager')
@observer
class Gradebook extends Component {
  constructor(props) {
    super(props);
    // TODO remove, unused // this.GradebookCard = SatCoreComponent('GradebookCard');
    // TODO remove, unused // this.GradebookDataBar = SatCoreComponent('GradebookDataBar');
    this.BreadCrumbs = SatCoreComponent('BreadCrumbs');
    this.DocReaderModal = SatCoreComponent('DocReaderModal');
    this.FileViewerModal = SatCoreComponent('FileViewerModal');
    this.FullscreenModal = SatCoreComponent('FullscreenModal');
    this.GradebookHeader = SatCoreComponent('GradebookHeader');
    this.GradebookPlacementAddToClassroomModal = SatCoreComponent('GradebookPlacementAddToClassroomModal');
    this.GradebookPlacementAddToGroupModal = SatCoreComponent('GradebookPlacementAddToGroupModal');
    this.GradebookTable = SatCoreComponent('GradebookTable');
    this.TakeAsStudentModal = SatCoreComponent('TakeAsStudentModal');
    this.state = {
      loading: true,
      placementAddToClassroomModalOpen: false,
      placementAddToGroupModalOpen: false,
      showAnswers: false,
      takeAsStudentModalOpen: false,
      takeAsStudentLessonShowing: false,
      takeAsStudentUrl: null,
      takeAsStudentContentName: null,
      takeAsStudentCreatedActivityId: null
    };
  }

  async componentDidMount() {
    const { classroomManager, courseManager, engagementManager, gradebookManager, navigationManager, userManager, t } = this.props;
    const urlParams = new URLSearchParams(window.location.search);

    gradebookManager.setForceRefreshAllGradebookData(false);

    // Zero state: navigated to gradebook with no assignments for this resource.
    const assignmentIdParam = urlParams.get('assignmentId');
    const hasAssignmentId = !!assignmentIdParam && assignmentIdParam !== 'undefined';
    if (!hasAssignmentId) {
      const routerState = this.props.history?.location?.state || {};
      const classroomId = urlParams.get('classroomId');
      // Ensure classroom is loaded — it may not be in the map yet if we skipped the normal fetch path.
      if (classroomId) {
        classroomManager.setCurrentClassroomId(classroomId);
        await classroomManager.fetchClassroomRoster(classroomId);
      }
      const classroom = classroomId ? classroomManager.getClassroom(classroomId) : null;
      gradebookManager.setActiveGradebookType(gradebookManager.activeGradebookTypeOnGradebookComponentMount || GRADEBOOK_TYPE.AGGREGATE);
      gradebookManager.setActiveGradebookTypeOnGradebookComponentMount(null);
      gradebookManager.setActiveGradebookTableOnGradebookComponentMount(null);
      // Avoid leaking a previous resource's assignment into zero-state actions
      // (e.g. Record student responses launching the wrong lesson).
      gradebookManager.setCurrentAssignment(null);
      navigationManager.setView(VIEW_SELECTION.GRADEBOOK);
      const zeroStateElementId = routerState.elementId || urlParams.get('elementId') || null;
      // Pre-populate from router state if available (set by GradebookNavigationService).
      const hasInfoFromRouterState = !!(routerState.resourceContentItemDescription || routerState.resourceInfo);
      this.setState({
        loading: false,
        isZeroState: true,
        zeroStateResourceName: routerState.resourceName || null,
        zeroStateResourceDescription: routerState.resourceContentItemDescription || null,
        zeroStateResourceInfo: routerState.resourceInfo || null,
        zeroStateEntityTypeId: routerState.entityTypeId || null,
        zeroStatePdfDeliveryFormat: routerState.pdfDeliveryFormat || null,
        zeroStateHasInfo: hasInfoFromRouterState,
        zeroStateClassroomName: classroom?.name || null,
        zeroStateElementId,
        zeroStateContentItemId: routerState.contentItemId || null,
        zeroStateCourseContentItemId: routerState.courseContentItemId || null,
        zeroStateCourseResourceParentElementId: routerState.courseResourceParentElementId || null,
        zeroStateClassroomId: classroomId || null,
        zeroStateImageUrl: routerState.imageUrl || null,
      });
      // If router state didn't carry description data, fetch from the element directly.
      const needsFallbackFetch = zeroStateElementId && (!hasInfoFromRouterState || !routerState.entityTypeId);
      if (needsFallbackFetch) {
        const element = await courseManager.fetchCourseResourceElement(zeroStateElementId);
        if (element) {
          const stateUpdate = {};
          if (!hasInfoFromRouterState) {
            stateUpdate.zeroStateResourceDescription = element.contentItemDescription || null;
            stateUpdate.zeroStateResourceInfo = element.resourceInfo || null;
            stateUpdate.zeroStateHasInfo = !!(element.contentItemDescription || element.resourceInfo);
          }
          if (!routerState.entityTypeId) {
            stateUpdate.zeroStateEntityTypeId = element.type || element.entityTypeId || element.contentItemEntityTypeId || null;
          }
          if (!routerState.pdfDeliveryFormat) {
            stateUpdate.zeroStatePdfDeliveryFormat = element.pdfDeliveryFormat || null;
          }
          this.setState(stateUpdate);
        }
      }
      return;
    }

    const activeGradebookType = gradebookManager.activeGradebookTypeOnGradebookComponentMount || GRADEBOOK_TYPE.AGGREGATE;

    let activeGradebookTable = gradebookManager.activeGradebookTableOnGradebookComponentMount || gradebookManager.defaultGradebookTable;

    const assignment = await this.getGradebookCurrentAssignment();
    const entityTypeId = assignment?.contentItemEntityTypeId || assignment?.entityTypeId;

    if (assignment && (entityTypeId === CONTENT_ITEM_TYPES.LESSON || entityTypeId === CONTENT_ITEM_TYPES.ASSESSMENT)) {
      const courseElement = await courseManager.fetchCourseResourceElement(assignment.courseResourceElementId);
      if (courseElement && courseElement.aiGradable && userManager.user.publisherAiEnabled && userManager.user.institutionAiEnabled) {
        gradebookManager.setIsAIGradingEnabled(true);
      }
    }

    if (activeGradebookTable === GRADEBOOK_CONTEXT.DETAILS || activeGradebookTable === GRADEBOOK_CONTEXT.LIKERT) {
      // we need to ensure 'details' view is allowed for the current assignment

      const entityTypeId = assignment.contentItemEntityTypeId || assignment.entityTypeId;

      const isAssessment = entityTypeId === 'assessment';
      const isLesson = entityTypeId === 'lesson';
      const isLearnosity = entityTypeId?.includes?.('learnosity');

      const { allowLearnosityGradebookDetail } = gradebookManager;

      const allowGradebookDetails = isAssessment || isLesson || (isLearnosity && allowLearnosityGradebookDetail);

      if (!allowGradebookDetails) {
        // gradebook 'details' view is not allowed, so here we set activeGradebookTable to 'summary' instead
        activeGradebookTable = GRADEBOOK_CONTEXT.SUMMARY;
      }
    }

    if (activeGradebookTable === GRADEBOOK_CONTEXT.ENGAGEMENT) {
      engagementManager.clearEngagementTable();
      await this.refreshAllGradebookEngagementData();
    }

    gradebookManager.setActiveGradebookType(activeGradebookType);
    gradebookManager.setActiveGradebookTable(activeGradebookTable);

    gradebookManager.setActiveGradebookTypeOnGradebookComponentMount(null);
    gradebookManager.setActiveGradebookTableOnGradebookComponentMount(null);

    gradebookManager.setActiveGradebookDetailsHorizontalScoresPage(1);

    gradebookManager.setObservationalGraderToggleStatus(OBSERVATIONAL_GRADER_TOGGLE_STATUS.INACTIVE);

    navigationManager.setView(VIEW_SELECTION.GRADEBOOK);

    if (navigationManager.pathList) {
      const reportId = urlParams.get('reportId');

      const currentRouterUrl = `${window.location.pathname}${window.location.search}`;

      if (reportId) {
        await ReportBreadcrumbService.initBreadcrumbs();
        await navigationManager.setBreadcrumb({
          path: {
            name: t('allAssignments'),
            routerUrl: currentRouterUrl
          }
        });
      } else {
        const pathName = navigationManager.pathList?.[0]?.name;
        const fromAssignments = urlParams.get('fromAssignments') === 'true';

        if (pathName === t('classrooms') || pathName === t('assignmentsLabel')) {
          if (pathName === t('classrooms') && navigationManager.pathList.length === 1 && fromAssignments) {
            // breadcrumb state was lost; reinitialize and assume 'Assignments' is the root
            navigationManager.clearAllPaths(true);
            await navigationManager.setBreadcrumb({
              path: {
                name: t('assignmentsLabel'),
                routerUrl: `/assignments?view=${VIEW_SELECTION.ASSIGNMENTS}`
              }
            });
          }
          await navigationManager.setBreadcrumb({
            fromView: VIEW_SELECTION.GRADEBOOK,
            path: {
              name: t('gradebookLabel', 'Gradebook'),
              routerUrl: currentRouterUrl
            }
          });
        }
      }
    }

    const classroomId = urlParams.get('classroomId');

    if (classroomId) {
      classroomManager.setCurrentClassroomId(classroomId);
      await classroomManager.fetchClassroomRoster(classroomId);
    }
    await this.refreshAllGradebookData();

    // Keep classroom manager in sync for flows that read currentClassroomId (e.g. Present)
    if (!classroomManager.currentClassroomId && gradebookManager.currentAssignment) {
      const assignment = gradebookManager.currentAssignment;
      const assignmentClassroomId = assignment.classroomId
        || (assignment.domainTypeId === 'classroom' ? assignment.domainId : null);
      if (assignmentClassroomId) {
        classroomManager.setCurrentClassroomId(assignmentClassroomId);
      }
    }

    this.setState({
      loading: false,
      currentClassroomId: classroomId || classroomManager.currentClassroomId
    });
  }

  async componentDidUpdate() {
    const { gradebookManager } = this.props;
    if (gradebookManager.forceRefreshAllGradebookData) {
      gradebookManager.setForceRefreshAllGradebookData(false);
      await this.refreshAllGradebookData();
    }
  }

  componentWillUnmount() {
    const { gradebookManager, productManager } = this.props;
    clearInterval(this.timer);
    this.timer = null;
    productManager.setShouldShowExpiredLicenseWarning(false);
    gradebookManager.resetSortForAllGradebookTableContexts();
  }

  // re-fetch the count of activities being created until there are none
  poll = async () => {
    const { gradebookManager } = this.props;

    const prevCount = gradebookManager.assignmentsProcessingCount;

    const assignment = await this.getGradebookCurrentAssignment();
    await gradebookManager.fetchAssignmentsProcessingCount(assignment);

    const newCount = gradebookManager.assignmentsProcessingCount;

    if (gradebookManager.assignmentsProcessingCount === 0) {
      clearInterval(this.timer);
      this.timer = null;
    }

    if (prevCount !== newCount) {
      this.refreshAllGradebookData();
    }
  }

  refreshAllGradebookData = async (page = null) => {
    const FIRST_PAGE = 0;
    page = page >= 0 ? page : FIRST_PAGE;
    this.setState({ loading: true });

    const { gradebookManager, history } = this.props;

    const {
      activeGradebookTable,
      isGradebookDetails,
      isGradebookLikert,
      isGradebookPlacement,
      isGradebookStandards,
      isGradebookSummary,
      isGradebookTyping
    } = gradebookManager;

    gradebookManager.setGradebookManagerLoadingFlagWhileHidingViewActive(true);

    gradebookManager.setGradebookTableRowsTotalFromBackend(null);
    gradebookManager.setHasMoreAggregateTableRows(null);

    gradebookManager.setGradebookDetails(null);

    const urlParams = new URLSearchParams(window.location.search);
    const assignmentIdParam = urlParams.get('assignmentId');
    if (assignmentIdParam && assignmentIdParam !== 'undefined') {
      const assignment = await this.getGradebookCurrentAssignment();

      if (!assignment) {
        console.error('Gradebook.refreshAllGradebookData: assignment not found');
        // eslint-disable-next-line no-alert
        alert('An error occurred while attempting to fetch data. Please try again.');
        history.push('/');
        return;
      }

      const sortObj = gradebookManager.gradebookTableSortMap.get(activeGradebookTable) || {};

      const sortColumn = sortObj.sortColumn || null;
      const sortDirection = sortObj.sortDirection || 'ascending';

      /* REFRESH GRADEBOOK TABLE, WHICHEVER IS ACTIVE */
      const pageSize = undefined, clearFirst = true;
      if (isGradebookSummary) {
        gradebookManager.setAllowLoadMoreAggregateTableRows(false);

        // TODO remove
        // if (GradebookSummaryService.isSpecialAggregateGradebookSummarySortCase(sortColumn)) {
        //   gradebookManager.reverseGradebookTableSortDirection(sortDirection);
        //   await GradebookSummaryService.handleSpecialAggregateGradebookSummarySortCase(sortColumn);
        // } else {
        const functions = {
          // TODO remove
          // isSpecialAggregateGradebookSummarySortCase: GradebookSummaryService.isSpecialAggregateGradebookSummarySortCase
        };
        const summaryFetches = [
          gradebookManager.fetchAggregateGradebookData(
            assignment, sortColumn, sortDirection, page,
            pageSize, clearFirst, functions
          )
        ];
        if (gradebookManager.allowPlacement && gradebookManager.placementCategoryId) {
          summaryFetches.push(
            gradebookManager.fetchPlacementStandardsAndTags(assignment.contentItemId)
          );
        }
        await Promise.all(summaryFetches);
        // }

        await gradebookManager.fetchAssignmentsProcessingCount(assignment);
        if (gradebookManager.assignmentsProcessingCount > 0 && !this.timer) {
          this.timer = setInterval(() => this.poll(), 20000);
        }

        gradebookManager.setAllowLoadMoreAggregateTableRows(true);
      } else if (isGradebookDetails || isGradebookLikert || isGradebookTyping) {
        gradebookManager.clearAssignmentInstances();
        gradebookManager.clearGradebookDetails();

        // TODO remove
        // if (GradebookDetailsService.isSpecialAggregateGradebookDetailsSortCase(sortColumn)) {
        //   gradebookManager.reverseGradebookTableSortDirection(sortDirection);
        //   await GradebookDetailsService.handleSpecialAggregateGradebookDetailsSortCase(sortColumn);
        // } else {
        const pageSize = undefined;
        const clearStudentsFirst = true;
        const clearAllFirst = true;
        const functions = {
          // TODO remove
          // isSpecialAggregateGradebookDetailsSortCase: GradebookDetailsService.isSpecialAggregateGradebookDetailsSortCase
        };
        await gradebookManager.fetchAggregateGradebookDetails({
          assignment,
          clearAllFirst,
          clearStudentsFirst,
          functions,
          page,
          pageSize,
          sortDirection,
          sortField: sortColumn
        });
        // }
      } else if (isGradebookStandards) {
        await gradebookManager.fetchAggregateGradebookStandards(assignment);
      } else if (isGradebookPlacement) {
        gradebookManager.setAllowLoadMoreAggregateTableRows(false);
        await gradebookManager.fetchAggregateGradebookData(
          assignment, sortColumn, sortDirection, page, undefined, true, {}
        );
        gradebookManager.setAllowLoadMoreAggregateTableRows(true);
      }
      this.refreshGradebookState(assignment);
      gradebookManager.setGradebookManagerLoadingFlagWhileHidingViewActive(false);
    }
  }

  getGradebookCurrentAssignment = async () => {
    const { assignmentManager, gradebookManager } = this.props;

    const urlParams = new URLSearchParams(window.location.search);
    const assignmentId = urlParams.get('assignmentId');

    if (!gradebookManager.currentAssignment || (gradebookManager.currentAssignment.id !== assignmentId)) {
      const assignment = await gradebookManager.fetchGradebookData(assignmentId, {}, true);
      return assignment;
    }
    let assignment = gradebookManager.currentAssignment;
    if (!assignment) {
      assignment = assignmentManager.fetchActivity(assignmentId);
    }
    return assignment;
  }

  fetchCourseActivityInformation = async (assignment) => {
    const { assignmentManager } = this.props;
    const contentImageUrl = ImageService.getImageUrl(assignment);

    const result = await assignmentManager.fetchCourseActivityInformation(assignment.id);
    if (result) {
      const title = result.webTitle ? result.webTitle : result.contentName;
      this.handleOpenInfo(
        title,
        result.contentDescription,
        result.resourceInformation, contentImageUrl,
        null // don't show assignment instruction since the gradebook represents multiple assignments.
      );
    }
  }

  handlePresentFromZeroState = () => {
    const { classroomManager, dialogManager } = this.props;
    const {
      zeroStateClassroomId,
      zeroStateContentItemId,
      zeroStateCourseContentItemId,
      zeroStateElementId,
    } = this.state;
    const classroomId = zeroStateClassroomId
      || this.state.currentClassroomId
      || ClassroomService.getCurrentClassroomId({ alsoUpdateObservableWithUrlParamIfNull: true });
    if (classroomId) {
      classroomManager.setCurrentClassroomId(classroomId);
    }
    const courseElement = {
      contentItemId: zeroStateContentItemId,
      courseContentItemId: zeroStateCourseContentItemId,
      elementId: zeroStateElementId,
      classroomId,
    };
    dialogManager.setOpenDialog(DIALOG_NAMES.PRESENT_ASSIGNMENT, {
      classroomId,
      courseElement,
      handlePresent: this.handlePresent
    });
  }

  navigateZeroStateToStandardGradebook = (assignmentId) => {
    const { gradebookManager, history, navigationManager } = this.props;
    const { zeroStateClassroomId } = this.state;
    const classroomId = zeroStateClassroomId;
    const routerUrl = `/gradebook?assignmentId=${assignmentId}&view=${VIEW_SELECTION.GRADEBOOK}&classroomId=${classroomId}`;

    // Push the URL first so window.location.search is updated before refreshAllGradebookData reads it.
    history.push(routerUrl);

    // Pushing to the same route doesn't remount the component, so we transition manually.
    const activeGradebookType = gradebookManager.activeGradebookTypeOnGradebookComponentMount || GRADEBOOK_TYPE.AGGREGATE;
    const activeGradebookTable = gradebookManager.activeGradebookTableOnGradebookComponentMount
      || gradebookManager.defaultGradebookTable
      || GRADEBOOK_CONTEXT.SUMMARY;

    gradebookManager.setActiveGradebookType(activeGradebookType);
    gradebookManager.setActiveGradebookTable(activeGradebookTable);
    gradebookManager.setActiveGradebookTypeOnGradebookComponentMount(null);
    gradebookManager.setActiveGradebookTableOnGradebookComponentMount(null);
    gradebookManager.setActiveGradebookDetailsHorizontalScoresPage(1);
    navigationManager.setView(VIEW_SELECTION.GRADEBOOK);

    this.setState({
      isZeroState: false,
      loading: true,
      currentClassroomId: classroomId,
      // Clear zero-state fields
      zeroStateResourceName: null,
      zeroStateResourceDescription: null,
      zeroStateResourceInfo: null,
      zeroStateEntityTypeId: null,
      zeroStatePdfDeliveryFormat: null,
      zeroStateHasInfo: false,
      zeroStateClassroomName: null,
      zeroStateElementId: null,
      zeroStateContentItemId: null,
      zeroStateCourseContentItemId: null,
      zeroStateCourseResourceParentElementId: null,
      zeroStateClassroomId: null,
    }, async () => {
      await this.refreshAllGradebookData();
      this.setState({ loading: false });
    });
  }

  handleInfoIconFromZeroState = () => {
    const {
      zeroStateResourceName,
      zeroStateResourceDescription,
      zeroStateResourceInfo,
    } = this.state;
    this.handleOpenInfo(
      zeroStateResourceName,
      zeroStateResourceDescription,
      zeroStateResourceInfo,
      null,
      null
    );
  }

  handleOpenInfo = (
    infoName,
    contentItemDescription,
    resourceInfo,
    contentItemImageUrl,
    instruction
  ) => {
    const { dialogManager } = this.props;

    dialogManager.setOpenDialog(DIALOG_NAMES.INFO, {
      contentItemDescription,
      contentItemImageUrl,
      infoName,
      resourceInfo,
      studentInstruction: instruction
    }, () => dialogManager.closeDialog(DIALOG_NAMES.INFO));
  }

  handleAnswerKey = async (assignment) => {
    const { contentManager, userManager } = this.props;
    const { currentClassroomId } = this.state;
    const option = await contentManager.getOptionsForAnswerKey(
      assignment.attachmentContentItemId, CONTENT_ITEM_TYPES.PDF_RESOURCE,
      currentClassroomId, origin, userManager.isTeacher, userManager.userId
    );
    const { playerType, viewUrl } = option;

    if (playerType === null && viewUrl !== null) {
      window.open(viewUrl, '_blank');
      return;
    }
    await contentManager.configPlayerWindow(playerType, window, this.hideIframeFromOuterClick);
  }

  handlePlacementAddToClassroom = () => {
    this.setState({ placementAddToClassroomModalOpen: true });
  }

  handleClosePlacementAddToClassroomModal = () => {
    this.setState({ placementAddToClassroomModalOpen: false });
  }

  handlePlacementAddToClassroomNext = (selectedClassroom) => {
    // TODO: implement add selected students to the chosen classroom
    this.setState({ placementAddToClassroomModalOpen: false });
  }

  handlePlacementAddToGroup = () => {
    this.setState({ placementAddToGroupModalOpen: true });
  }

  handleClosePlacementAddToGroupModal = () => {
    this.setState({ placementAddToGroupModalOpen: false });
  }

  handleOpenTakeAsStudentModal = () => {
    const { classroomManager } = this.props;
    const classroomId = this.state.zeroStateClassroomId
      || classroomManager.currentClassroomId
      || this.state.currentClassroomId;
    if (classroomId) {
      classroomManager.setCurrentClassroomId(classroomId);
      classroomManager.fetchClassroomRoster(classroomId);
    }
    this.setState({ takeAsStudentModalOpen: true });
  }

  handleCloseTakeAsStudentModal = () => {
    this.setState({ takeAsStudentModalOpen: false });
  }

  handleCloseTakeAsStudentLesson = () => {
    const { takeAsStudentCreatedActivityId } = this.state;
    this.setState({
      takeAsStudentLessonShowing: false,
      takeAsStudentUrl: null,
      takeAsStudentContentName: null,
      takeAsStudentCreatedActivityId: null
    }, () => {
      if (takeAsStudentCreatedActivityId) {
        this.navigateZeroStateToStandardGradebook(takeAsStudentCreatedActivityId);
      }
    });
  }

  handleTakeAsStudentConfirm = async (student) => {
    const { assignmentManager, classroomManager, courseManager, gradebookManager } = this.props;
    const { currentAssignment } = gradebookManager;
    const {
      isZeroState,
      zeroStateClassroomId,
      zeroStateContentItemId,
      zeroStateCourseContentItemId,
      zeroStateElementId,
    } = this.state;

    if (!student) {
      this.setState({ takeAsStudentModalOpen: false });
      return;
    }

    // Prefer existing assignment; in zero state use resource/course context from URL/router state.
    // Important: do NOT fall back to gradebookManager.currentAssignment while in zero state —
    // that can still be a previous resource's assignment and would launch the wrong lesson.
    const contentItemId = isZeroState
      ? zeroStateContentItemId
      : (currentAssignment?.contentItemId || zeroStateContentItemId);
    const courseResourceElementId = isZeroState
      ? zeroStateElementId
      : (currentAssignment?.courseResourceElementId || zeroStateElementId);
    const classroomId = isZeroState
      ? (zeroStateClassroomId || classroomManager.currentClassroomId || this.state.currentClassroomId)
      : (currentAssignment?.classroomId
        || zeroStateClassroomId
        || classroomManager.currentClassroomId
        || this.state.currentClassroomId);
    const courseContentItemId = isZeroState
      ? (zeroStateCourseContentItemId || courseManager.currentCourseId)
      : (currentAssignment?.courseContentItemId
        || zeroStateCourseContentItemId
        || courseManager.currentCourseId);

    if (!contentItemId || !courseResourceElementId || !classroomId || !courseContentItemId) {
      this.setState({ takeAsStudentModalOpen: false });
      return;
    }

    // Always create a fresh assignment for the student (same as course card behavior)
    // so the recording is a clean slate rather than modifying an existing instance.
    const success = await assignmentManager.presentCreateAssignmentForStudent(
      contentItemId,
      courseResourceElementId,
      classroomId,
      courseContentItemId,
      student.userId
    );

    if (!success) {
      this.setState({ takeAsStudentModalOpen: false });
      return;
    }

    // StudentActivityDto: { id: instanceId, activityId, ... }
    const assignment = assignmentManager.getLastAddedAssignment();
    if (!assignment) {
      this.setState({ takeAsStudentModalOpen: false });
      return;
    }

    const activityId = assignment.activityId || assignment.id;
    const activityInstanceId = assignment.id;

    const authKey = await Auth.getUserAuthKey(student.userId);

    if (authKey) {
      const viewUrl = `${Auth.lesson}/activityMode?contentItemId=${assignment.contentItemId
        }&courseId=${assignment.courseContentItemId}&entityTypeId=${assignment.contentItemEntityTypeId
        }&activityId=${activityId}&activityInstanceId=${activityInstanceId
        }&appUrl=${encodeURIComponent(window.location.origin)}#${authKey}`;
      this.setState({
        takeAsStudentModalOpen: false,
        takeAsStudentLessonShowing: true,
        takeAsStudentUrl: viewUrl,
        takeAsStudentContentName: `${student.firstName} ${student.lastName}`,
        // After closing the player from zero state, enter the new assignment gradebook.
        takeAsStudentCreatedActivityId: isZeroState ? activityId : null
      });
    } else {
      this.setState({ takeAsStudentModalOpen: false });
    }
  }

  handleExportGradebook = async () => {
    const { dialogManager, gradebookManager, exportManager } = this.props;

    const assignmentId = gradebookManager.currentAssignmentId;
    const { currentAssignment } = gradebookManager;

    const { allowExportFullGradebookStandards } = exportManager;
    const { activeGradebookTable, allowGradebookStandards } = gradebookManager;

    if (allowGradebookStandards && allowExportFullGradebookStandards && activeGradebookTable !== 'standards') {
      // we need to determine if we should show 'Standards Export' radio option for ExportGradesModal
      // ---
      // ensure we have latest gradebook standards data before opening ExportGradesModal
      this.setState({ loading: true });
      await gradebookManager.fetchAggregateGradebookStandards(currentAssignment);
      this.setState({ loading: false });
    }

    dialogManager.setOpenDialog(DIALOG_NAMES.EXPORT_ASSIGNMENT, {
      ...this.props,
      assignment: currentAssignment,
      assignmentId,
      isFromGradebook: true
    }, this.closeExportAssignment);
  }

  closeExportAssignment = () => {
    const { dialogManager } = this.props;
    dialogManager.closeDialog(DIALOG_NAMES.EXPORT_ASSIGNMENT);
  }

  handlePresentAssignment = (assignment) => {
    const { classroomManager, dialogManager } = this.props;
    const classroomId = assignment?.classroomId
      || (assignment?.domainTypeId === 'classroom' ? assignment.domainId : null)
      || this.state.currentClassroomId
      || ClassroomService.getCurrentClassroomId({ alsoUpdateObservableWithUrlParamIfNull: true });
    if (classroomId) {
      classroomManager.setCurrentClassroomId(classroomId);
    }
    const courseElement = {
      contentItemId: assignment.contentItemId,
      courseContentItemId: assignment.courseContentItemId,
      elementId: assignment.courseResourceElementId,
      classroomId,
    };
    dialogManager.setOpenDialog(DIALOG_NAMES.PRESENT_ASSIGNMENT, {
      classroomId,
      courseElement,
      contentImageUrl: ImageService.getImageUrl(assignment),
      handlePresent: this.handlePresent
    });
  }

  handlePresent = async (params) => {
    const {
      assignmentManager, classroomManager, contentManager, courseManager, userManager
    } = this.props;

    const contentMode = params.contentMode || CONTENT_MODE.PREVIEW;
    const assignment = params.assignment || null;
    const publisherModeId = params.publisherModeId || null;
    let option = null;

    if (userManager.isTeacher && contentMode === CONTENT_MODE.PREVIEW) {
      option = await contentManager.getOptionsForTeacherPreviewAssigned(
        assignment, window.location.origin, userManager.isTeacher, userManager.userId, publisherModeId);
    } else if (userManager.isTeacher && contentMode === CONTENT_MODE.PRESENT) {
      const today = new Date();
      const todayWithBuffer = new Date();
      todayWithBuffer.setHours(today.getHours() + 1);
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);

      const activityContentStr = JSON.stringify([{
        contentItemId: assignment.contentItemId,
        courseResourceElementId: assignment.courseResourceElementId
      }]);
      await assignmentManager.createAssignment({
        activityContent: activityContentStr,
        assignEntityIds: userManager.userId,
        assignEntityTypeId: ASSIGNMENT_TYPE.TEACHER,
        classroomId: classroomManager.currentClassroomId,
        courseContentItemId: courseManager.currentCourseId,
        endRealDate: tomorrow,
        instruction: '',
        startRealDate: todayWithBuffer,
        status: ASSIGNMENT_STATUS.PRESENTATION
      });
      const presentAssignment = await assignmentManager.getLastAddedAssignment();
      option = await contentManager.getOptionsForTeacherPresent(
        presentAssignment.contentItemId, presentAssignment,
        presentAssignment.entityTypeId, window.location.origin, userManager.isTeacher);
    } else if (userManager.isTeacher && (contentMode === CONTENT_MODE.ASSESS || contentMode === CONTENT_MODE.TEACHER_PRESENT)) {
      const activityId = assignment.activityId || assignment.id;
      const activityInstanceId = assignment.activityInstanceId || assignment.id;
      if (ASSIGNMENT_STATUS.getCheckableStatuses().includes(assignment.status)) {
        const isReady = await assignmentManager.checkActivity(activityId);
        if (isReady) {
          const isOk = await assignmentManager.startActivity(activityInstanceId, activityId, params.turnInLate);
          if (isOk === false) return false;
        } else {
          return false;
        }
      }
      if (contentMode === CONTENT_MODE.TEACHER_PRESENT) {
        const classroomId = assignment.classroomId
          || (assignment.domainTypeId === 'classroom' ? assignment.domainId : null)
          || classroomManager.currentClassroomId
          || this.state.currentClassroomId
          || null;
        contentManager.setIsTeacherPresenting(true);
        contentManager.setPresentationContext({
          activityId,
          activityInstanceId,
          assignmentId: assignment.id,
          classroomId
        });
      }
      option = await contentManager.getOptionsForStudentAssessment(
        assignment, window.location.origin, userManager.isTeacher, userManager.userId);
    }

    if (option) {
      const name = assignment
        ? assignment.contentItemName || assignment.resourceWebTitle || assignment.name || ''
        : '';
      await this.launchContentOption(option, name);
    }
    return true;
  }

  launchContentOption = async (option, contentName = '') => {
    const { contentManager, dialogManager } = this.props;
    const { playerType, viewUrl, isFlowpaper } = option;

    if (playerType === null && viewUrl) {
      window.open(viewUrl, '_blank');
      return;
    }

    if (playerType === PLAYER_TYPES.CONTENT_PREVIEW_PLAYER) {
      dialogManager.setOpenDialog(DIALOG_NAMES.CONTENT_PREVIEW, {
        resourceName: contentName
      }, () => dialogManager.closeDialog(DIALOG_NAMES.CONTENT_PREVIEW));
      return;
    }

    contentManager.configPlayerWindow(playerType, window, this.hideViewContentFromMessage);

    if (playerType === PLAYER_TYPES.LESSON_PLAYER) {
      contentManager.setLessonPlayerShowing(true);
    }

    this.setState({
      viewContentDocreaderShowing: playerType === PLAYER_TYPES.DOCREADER_VIEWER,
      viewContentFileViewerShowing: playerType === PLAYER_TYPES.FILE_VIEWER,
      viewContentIsFlowpaper: isFlowpaper,
      viewContentLessonShowing: playerType === PLAYER_TYPES.LESSON_PLAYER,
      viewContentName: contentName,
      viewContentUrl: viewUrl,
    });
  }

  handleViewContent = async () => {
    const { contentManager, dialogManager, gradebookManager, userManager } = this.props;
    const { currentAssignment } = gradebookManager;
    if (!currentAssignment) return;

    const option = await contentManager.getOptionsForTeacherPreviewAssigned(
      currentAssignment, window.location.origin, userManager.isTeacher, userManager.userId
    );
    if (!option) return;

    const { playerType, viewUrl, isFlowpaper } = option;

    if (playerType === null && viewUrl) {
      window.open(viewUrl, '_blank');
      return;
    }

    if (playerType === PLAYER_TYPES.CONTENT_PREVIEW_PLAYER) {
      dialogManager.setOpenDialog(DIALOG_NAMES.CONTENT_PREVIEW, {
        contentItemId: currentAssignment.contentItemId,
        contentItemType: currentAssignment.contentItemEntityTypeId || currentAssignment.entityTypeId,
        resourceName: currentAssignment.contentItemName || currentAssignment.resourceWebTitle || currentAssignment.name
      }, () => dialogManager.closeDialog(DIALOG_NAMES.CONTENT_PREVIEW));
      return;
    }

    const name = currentAssignment.contentItemName || currentAssignment.resourceWebTitle || currentAssignment.name || '';
    if (playerType === PLAYER_TYPES.LESSON_PLAYER) {
      CourseService.setCourseResourceItemIdForRelatedItems(currentAssignment.courseResourceElementId);
      CourseService.setCourseContentItemIdForRelatedItems(currentAssignment.courseContentItemId);
    }
    await this.launchContentOption(option, name);
  }

  hideViewContentFromMessage = (event) => {
    if ((event.origin === Auth.publisher || event.origin === Auth.lesson) && event.data === 'hideIframe') {
      this.closeViewContent();
      window.removeEventListener('message', this.hideViewContentFromMessage);
    }
  }

  closeViewContent = () => {
    const { assignmentManager, contentManager } = this.props;
    const {
      isZeroState,
      zeroStateClassroomId,
      zeroStateContentItemId,
      zeroStateCourseContentItemId,
      zeroStateElementId,
    } = this.state;

    const wasPresenting = contentManager.isTeacherPresenting;
    const presentationContext = contentManager.presentationContext || {};

    PresentationOptionsService.openIfPresenting({
      ...presentationContext,
      wasPresenting,
      onBackgroundRefresh: () => {
        if (!this.state.isZeroState) {
          this.refreshAllGradebookData();
        }
      }
    });
    if (!wasPresenting) {
      ContentService.setIsTeacherPresenting(false);
    }

    contentManager.setLessonPlayerShowing(false);
    ContentService.setLessonPlayerShowing(false);
    ContentService.setSatelliteTabShowing(false);
    CourseService.setCourseResourceItemIdForRelatedItems(null);
    CourseService.setCourseContentItemIdForRelatedItems(null);
    this.setState({
      viewContentDocreaderShowing: false,
      viewContentFileViewerShowing: false,
      viewContentIsFlowpaper: false,
      viewContentLessonShowing: false,
      viewContentName: null,
      viewContentUrl: null,
    }, async () => {
      if (!isZeroState) {
        this.refreshAllGradebookData();
      } else if (zeroStateElementId) {
        const assignments = await assignmentManager.fetchContentAssignments(
          zeroStateCourseContentItemId, zeroStateElementId, zeroStateClassroomId,
          zeroStateContentItemId, 0, false, true
        );
        const assignmentId = assignments?.[0]?.id;
        if (assignmentId) {
          this.navigateZeroStateToStandardGradebook(assignmentId);
        }
      }
    });
  }

  handleGotoCourse = () => {
    const { courseManager, history, gradebookManager, navigationManager } = this.props;
    const { currentAssignment } = gradebookManager;
    const { classroomId, courseContentItemId, courseResourceParentElementId } = currentAssignment;
    courseManager.setAllowFetchCourseData(true); // in case we were coming from reports and this wasn't reset
    courseManager.setAllowCourseTreeLeafFooter(false); // in case we were coming from reports and this wasn't reset
    navigationManager.navigateToCourseContent(classroomId, courseContentItemId, courseResourceParentElementId, history, false);
  }

  // TODO remove, unused (the gradebook is **always** aggregate now)
  // handleClickAggregateGradebookButton = async () => {
  //   const { gradebookManager, navigationManager, t } = this.props;
  //   gradebookManager.clearAssignmentInstances();
  //   if (gradebookManager.activeGradebookTable === 'details') {
  //     gradebookManager.setGradebookManagerLoadingFlagWhileHidingViewActive(true);
  //   }
  //   let paramStr = window.location.search;
  //   paramStr = paramStr || '';
  //   const routerUrl = `/gradebook${paramStr}`;
  //   await navigationManager.setBreadcrumb({
  //     fromView: VIEW_SELECTION.GRADEBOOK,
  //     path: {
  //       name: t('aggregateGradebook', 'All Assignments'),
  //       routerUrl
  //     }
  //   });
  //   await this.refreshAllGradebookData();
  // }

  handleEngagementButtonClick = async () => {
    const { gradebookManager } = this.props;

    // gradebookManager.clearAssignmentInstances();

    // if (gradebookManager.activeGradebookTable === 'details') {
    gradebookManager.setGradebookManagerLoadingFlagWhileHidingViewActive(true);
    // }
    // TODO remove, unused
    // let paramStr = window.location.search;
    // paramStr = paramStr || '';
    // const routerUrl = `/gradebook${paramStr}`;
    // await navigationManager.setBreadcrumb({
    //   fromView: VIEW_SELECTION.GRADEBOOK,
    //   path: {
    //     name: t('aggregateGradebook', 'All Assignments'),
    //     routerUrl
    //   }
    // });
    await this.refreshAllGradebookEngagementData();
  }

  refreshAllGradebookEngagementData = async (page = null) => {
    const FIRST_PAGE = 0;
    page = page >= 0 ? page : FIRST_PAGE;
    this.setState({ loading: true });

    const { gradebookManager, history } = this.props;

    gradebookManager.setGradebookManagerLoadingFlagWhileHidingViewActive(true);

    gradebookManager.setGradebookTableRowsTotalFromBackend(null);
    gradebookManager.setHasMoreAggregateTableRows(null);

    gradebookManager.setGradebookDetails(null);

    const urlParams = new URLSearchParams(window.location.search);
    const assignmentIdParam = urlParams.get('assignmentId');
    if (assignmentIdParam && assignmentIdParam !== 'undefined') {
      const assignment = await this.getGradebookCurrentAssignment();

      if (!assignment) {
        console.error('Gradebook.refreshAllGradebookData: assignment not found');
        // eslint-disable-next-line no-alert
        alert('An error occurred while attempting to fetch data. Please try again.');
        history.push('/');
        return;
      }

      gradebookManager.setAllowLoadMoreAggregateTableRows(false);
      await EngagementService.fetchAggregateGradebookEngagement(assignment);

      this.refreshGradebookState(assignment);
      gradebookManager.setAllowLoadMoreAggregateTableRows(true);
      gradebookManager.setGradebookManagerLoadingFlagWhileHidingViewActive(false);
    }
  }

  handleOpenAddAssignmentModal = async (data, isReassign = false) => {
    const { assignmentManager, classroomManager, contentManager, courseManager, dialogManager, gradebookManager } = this.props;
    this.setState({ loading: true });
    // data is either an activityInstance or assignment, so figure out which one.
    // the assignment passed in for bulk checked may not be one related to checked rows
    // so we are only using it for getting the content ids and defaults for the assign modal.
    const activityId = (data.activityId) ? data.activityId : data.id;
    const assignment = await assignmentManager.fetchActivity(activityId);
    let courseResourceElement = null;

    if (assignment) {
      // eslint-disable-next-line max-len, no-nested-ternary
      const classroomId = (assignment.classroomId) ? assignment.classroomId : (assignment.domainTypeId === 'classroom') ? assignment.domainId : null;
      classroomManager.setCurrentClassroomId(classroomId);
      courseResourceElement = await courseManager.fetchCourseResourceElement(assignment.courseResourceElementId);
    }
    const contentImageUrl = ImageService.getImageUrl(assignment);
    this.setState({ loading: false });
    if (assignment && courseResourceElement) {
      const contentItemEntityId = courseResourceElement.entityId;
      const contentItemEntityTypeId = assignment.entityTypeId || courseResourceElement.type || courseResourceElement.entityTypeId;
      const contentItemEntitySubTypeId = assignment.contentItemEntitySubTypeId || assignment.entitySubTypeId;
      let learnosityActivityResourceId = null;

      // If this is a learnosity activity get proper content item entityId to use as learnosityActivityResourceId
      if (contentItemEntityTypeId === CONTENT_ITEM_TYPES.LEARNOSITY_ACTIVITY_RESOURCE) {
        // Manager applies TTL / in-flight coalescing for viewContentItem
        await contentManager.fetchContentItem(courseResourceElement.entityId);
        const learnosityActivityResourceContentItem = contentManager.getContentItem(courseResourceElement.entityId);
        if (learnosityActivityResourceContentItem
          && learnosityActivityResourceContentItem.entityTypeId === CONTENT_ITEM_TYPES.LEARNOSITY_ACTIVITY_RESOURCE) {
          learnosityActivityResourceId = learnosityActivityResourceContentItem.entityId;
        }
      }

      dialogManager.setOpenDialog(DIALOG_NAMES.ADD_ASSIGNMENT, {
        ...this.props,
        alternateModeId: courseResourceElement.alternateModeIdOverride || courseResourceElement.alternateModeId,
        contentDueDate: ResourcePacingService.dueDate(courseResourceElement),
        contentDuration: ResourcePacingService.duration(courseResourceElement),
        contentImageUrl,
        contentItemEntityId,
        contentItemEntityTypeId,
        contentItemEntitySubTypeId,
        contentItemId: courseResourceElement.entityId || assignment.contentItemId,
        contentName: courseResourceElement.name || courseResourceElement.name || assignment.name,
        contentSubname: courseResourceElement.description,
        contentTimeframeStartDate: courseResourceElement.timeframeStartDate,
        contentTimeframeEndDate: courseResourceElement.timeframeEndDate,
        timeframeStartDateStr: courseResourceElement.timeframeStartDateStr,
        timeframeEndDateStr: courseResourceElement.timeframeEndDateStr,
        timeframeStartTimeStr: courseResourceElement.timeframeStartTimeStr,
        timeframeEndTimeStr: courseResourceElement.timeframeEndTimeStr,
        courseContentItemId: assignment.courseContentItemId,
        courseElementToggleDefaults: {
          includeInReports: courseResourceElement.includeInReportsOverride || courseResourceElement.includeInReports,
          scoresReleased: courseResourceElement.scoresReleasedOverride || courseResourceElement.scoresReleased,
          studentInstruction: courseResourceElement.instructionOverride || courseResourceElement.instruction,
          studentReview: courseResourceElement.studentReviewOverride || courseResourceElement.studentReview
        },
        courseResourceElementId: courseResourceElement.id || assignment.courseResourceElementId,
        // TODO remove // hideAllClassesAssignType: true,
        learnosityActivityResourceId,
        modeOverrideAllowed: courseResourceElement.modeOverrideAllowed,
        resourceMode: courseResourceElement.resourceMode,
        standards: courseResourceElement.standardsInfo
      }, () => {
        dialogManager.closeDialog(DIALOG_NAMES.ADD_ASSIGNMENT);
        gradebookManager.setGradebookManagerLoadingFlagWhileHidingViewActive(true);
        gradebookManager.clearBulkCheckedAssignmentMembers();
        this.setState({ loading: true });
        this.refreshAllGradebookData().then(() => {
          this.setState({ loading: false });
          gradebookManager.setGradebookManagerLoadingFlagWhileHidingViewActive(false);
        });
      });
    }
  }

  handleOpenEditBulkAssignmentModal = async (instanceInfo) => {
    const { assignmentManager, dialogManager, gradebookManager, history, courseManager } = this.props;
    const { currentClassroomId } = this.state;

    this.setState({ loading: true });
    AssignmentService.setBoundaryStartEndDates();

    const activityId = gradebookManager.bulkActivityIds?.[0] || instanceInfo.activityId;
    const assignment = await gradebookManager.fetchGradebookActivity(activityId, true);
    if (assignment) {
      // Update the assignment manager with this fresh assignment.
      assignmentManager.setAssignment(assignment);
    }

    const courseElement = await courseManager.fetchCourseResourceElement(assignment.courseResourceElementId);
    const contentImageUrl = ImageService.getImageUrl(assignment);
    this.setState({ loading: false });

    const { bulkActivityIds } = gradebookManager;

    const hasSelectedPresentationModeAssignment = !!bulkActivityIds?.find?.((bulkActivityId) => {
      const selectedAssignment = assignmentManager.getAssignment(bulkActivityId);

      const isPresentationMode = !!gradebookManager.isPresentationMode(selectedAssignment);
      return isPresentationMode;
    });

    dialogManager.setOpenDialog(DIALOG_NAMES.EDIT_BULK_ASSIGNMENT, {
      activityNumber: instanceInfo.activityNumber,
      assignmentId: assignment.id,
      bulkActivityIds,
      bulkActivityNumbers: gradebookManager.bulkActivityNumbers,
      classroomId: currentClassroomId,
      contentImageUrl,
      contentTimeframeEndDate: courseElement.timeframeEndDate,
      contentTimeframeStartDate: courseElement.timeframeStartDate,
      courseElement,
      hasSelectedPresentationModeAssignment,
      history,
      timeframeEndDateStr: courseElement.timeframeEndDateStr,
      timeframeEndTimeStr: courseElement.timeframeEndTimeStr,
      timeframeStartDateStr: courseElement.timeframeStartDateStr,
      timeframeStartTimeStr: courseElement.timeframeStartTimeStr
    }, () => {
      dialogManager.closeDialog(DIALOG_NAMES.EDIT_BULK_ASSIGNMENT);
      gradebookManager.setGradebookManagerLoadingFlagWhileHidingViewActive(true);
      this.setState({ loading: true });
      this.refreshAllGradebookData().then(() => {
        this.setState({ loading: false });
        gradebookManager.setGradebookManagerLoadingFlagWhileHidingViewActive(false);
      });
    });
  }

  hideIframe = () => {
    this.setState({ showAnswers: false });
  }

  refreshGradebookState(assignment) {
    this.setState({
      loading: false,
    });
  }

  handleAssignFromZeroState = async () => {
    const { courseManager, dialogManager } = this.props;
    const {
      zeroStateClassroomId, zeroStateContentItemId, zeroStateCourseContentItemId,
      zeroStateElementId, zeroStateImageUrl, zeroStateResourceName,
    } = this.state;

    // Without an elementId we can't build a proper courseElement — fall back to the course tree.
    if (!zeroStateElementId) {
      this.handleGotoCourse();
      return;
    }

    this.setState({ loading: true });
    let courseElement;
    try {
      courseElement = await courseManager.fetchCourseResourceElement(zeroStateElementId);
    } catch (e) {
      courseElement = null;
    }
    this.setState({ loading: false });

    if (!courseElement) {
      this.handleGotoCourse();
      return;
    }

    dialogManager.setOpenDialog(DIALOG_NAMES.ADD_ASSIGNMENT, {
      ...this.props,
      classroomId: zeroStateClassroomId,
      contentImageUrl: ImageService.getImageUrl({ imageUrl: zeroStateImageUrl }) || ImageService.getImageUrl(courseElement),
      contentItemId: zeroStateContentItemId || courseElement.contentItemId,
      contentItemEntityId: courseElement.contentItemEntityId,
      contentItemEntityTypeId: courseElement.contentItemEntityTypeId,
      contentItemEntitySubTypeId: courseElement.contentItemEntitySubTypeId,
      contentName: courseElement.name || zeroStateResourceName,
      contentSubname: courseElement.description,
      contentTimeframeStartDate: courseElement.timeframeStartDate,
      contentTimeframeEndDate: courseElement.timeframeEndDate,
      timeframeStartDateStr: courseElement.timeframeStartDateStr,
      timeframeEndDateStr: courseElement.timeframeEndDateStr,
      timeframeStartTimeStr: courseElement.timeframeStartTimeStr,
      timeframeEndTimeStr: courseElement.timeframeEndTimeStr,
      courseContentItemId: zeroStateCourseContentItemId || courseElement.courseContentItemId,
      courseElement,
      courseResourceElementId: zeroStateElementId,
      elementId: zeroStateElementId,
      alternateModeId: courseElement.alternateModeIdOverride || courseElement.alternateModeId,
      resourceMode: courseElement.resourceMode,
      modeOverrideAllowed: courseElement.modeOverrideAllowed,
      standards: courseElement.standardsInfo,
      courseElementToggleDefaults: {
        includeInReports: courseElement.includeInReportsOverride ?? courseElement.includeInReports ?? true,
        scoresReleased: courseElement.scoresReleasedOverride ?? courseElement.scoresReleased ?? true,
        studentInstruction: courseElement.instructionOverride ?? courseElement.instruction,
        studentReview: courseElement.studentReviewOverride ?? courseElement.studentReview ?? true,
      },
    }, async () => {
      const { assignmentManager: am, dialogManager: dm } = this.props;
      const {
        zeroStateClassroomId, zeroStateCourseContentItemId, zeroStateElementId, zeroStateContentItemId,
      } = this.state;
      dm.closeDialog(DIALOG_NAMES.ADD_ASSIGNMENT);
      let assignmentId = am.getLastAddedAssignment()?.id;
      if (!assignmentId) {
        // Fallback: fetch assignments for this element in case lastAddedAssignment wasn't stored.
        const assignments = await am.fetchContentAssignments(
          zeroStateCourseContentItemId, zeroStateElementId, zeroStateClassroomId,
          zeroStateContentItemId, 0, false, true
        );
        assignmentId = assignments?.[0]?.id;
      }
      if (assignmentId) {
        this.navigateZeroStateToStandardGradebook(assignmentId);
      }
    });
  }

  handleViewPdfFromZeroState = async () => {
    await this.handleViewContentFromZeroState(PDF_DELIVERY_FORMATS.PDF);
  }

  handleViewContentFromZeroState = async (overridePdfDeliveryFormat = undefined) => {
    const { classroomManager, contentManager, courseManager, userManager } = this.props;
    const {
      zeroStateClassroomId, zeroStateContentItemId, zeroStateCourseContentItemId,
      zeroStateElementId, zeroStateResourceName,
    } = this.state;

    if (!zeroStateElementId) {
      this.handleGotoCourse();
      return;
    }

    this.setState({ loading: true });
    let courseElement;
    try {
      courseElement = await courseManager.fetchCourseResourceElement(zeroStateElementId);
    } catch (e) {
      courseElement = null;
    }
    this.setState({ loading: false });

    if (!courseElement) return;

    const contentItemId = zeroStateContentItemId || courseElement.entityId || courseElement.contentItemId;
    const entityTypeId = courseElement.type;
    const pdfDeliveryFormat = overridePdfDeliveryFormat !== undefined ? overridePdfDeliveryFormat : (courseElement.pdfDeliveryFormat || null);
    const courseContentItemId = zeroStateCourseContentItemId || courseElement.courseContentItemId;
    const classroomId = zeroStateClassroomId || classroomManager.currentClassroomId;

    const option = await contentManager.getOptionsForTeacherPreview(
      contentItemId, entityTypeId, pdfDeliveryFormat, window.location.origin,
      userManager.canViewAsTeacher, userManager.userId,
      zeroStateElementId, classroomId, null, courseContentItemId
    );
    if (!option) return;

    const { playerType, viewUrl } = option;
    if (playerType === null && viewUrl) {
      window.open(viewUrl, '_blank');
      return;
    }

    const name = courseElement.name || zeroStateResourceName || '';
    if (playerType === PLAYER_TYPES.LESSON_PLAYER) {
      CourseService.setCourseResourceItemIdForRelatedItems(zeroStateElementId);
      CourseService.setCourseContentItemIdForRelatedItems(courseContentItemId);
    }
    await this.launchContentOption(option, name);
  }

  renderZeroStateGradebook = () => {
    const { t } = this.props;
    const {
      zeroStateClassroomId,
      zeroStateClassroomName,
      zeroStateContentItemId,
      zeroStateCourseContentItemId,
      zeroStateCourseResourceParentElementId,
      zeroStateEntityTypeId,
      zeroStateHasInfo,
      zeroStatePdfDeliveryFormat,
      zeroStateResourceName,
    } = this.state;

    const { BreadCrumbs, GradebookHeader } = this;
    const { courseManager, history, navigationManager } = this.props;

    const handleGotoCourse = () => {
      if (zeroStateCourseContentItemId) {
        courseManager.setAllowFetchCourseData(true);
        courseManager.setAllowCourseTreeLeafFooter(false);
        navigationManager.navigateToCourseContent(
          zeroStateClassroomId, zeroStateCourseContentItemId,
          zeroStateCourseResourceParentElementId, history, false
        );
      }
    };

    // Stub assignment gives GradebookHeader the name/classroom it needs without real assignment data.
    const stubAssignment = {
      contentItemId: zeroStateContentItemId || null,
      name: zeroStateResourceName || t('gradebookZeroStateDefaultTitle', 'Resource'),
      classroomName: zeroStateClassroomName || null,
    };

    const zeroStateTitle = UtilityService.reactHtmlParserWrapper(
      t('gradebookZeroStateTitle', 'No Assignments Yet')
    ).parsed;
    const zeroStateMessage = UtilityService.reactHtmlParserWrapper(
      t('gradebookZeroStateMessage', 'To get started, use the "I would like to:" button above to assign this resource to your students.')
    ).parsed;

    const { FullscreenModal, FileViewerModal, DocReaderModal } = this;
    const { userManager } = this.props;

    return (
      <Container className='gradebook-root-container grade-book-view aggregate summary' fluid>
        <div className='gradebook-container aggregate summary'>
          <div className='gradebook-breadcrumbs-wrapper'>
            <BreadCrumbs history={history} />
          </div>
          <div className='gradebook-inner-container aggregate summary'>
            <div className='gradebook-zero-state'>
              <GradebookHeader
                {...this.props}
                assignment={stubAssignment}
                assignable
                assignmentEntityTypeId={zeroStateEntityTypeId}
                isZeroState
                onClickAssignButton={this.handleAssignFromZeroState}
                onClickGotoCourse={handleGotoCourse}
                onClickInfoIconButton={zeroStateHasInfo ? this.handleInfoIconFromZeroState : undefined}
                onClickPresentAssignment={this.handlePresentFromZeroState}
                onClickRecordStudentResponse={this.handleOpenTakeAsStudentModal}
                onClickViewContent={this.handleViewContentFromZeroState}
                onClickViewPdf={this.handleViewPdfFromZeroState}
                pdfDeliveryFormat={zeroStatePdfDeliveryFormat} />
              {/* Zero state body — replaces the grid */}
              <div className='gradebook-zero-state-body'>
                <h2>{zeroStateTitle}</h2>
                <p>{zeroStateMessage}</p>
              </div>
            </div>
          </div>
        </div>
        {this.state.viewContentLessonShowing ? (
          <FullscreenModal
            closeIframeCallback={this.closeViewContent}
            contentName={this.state.viewContentName}
            isTeacher={userManager.isTeacher}
            page='lesson-player'
            url={this.state.viewContentUrl} />
        ) : null}
        {this.state.viewContentFileViewerShowing ? (() => {
          return (
            <FileViewerModal
              closeModalCallback={this.closeViewContent}
              contentMode={CONTENT_MODE.PREVIEW}
              contentName={this.state.viewContentName}
              isFlowpaper={this.state.viewContentIsFlowpaper}
              isTeacher={userManager.isTeacher}
              page='file-viewer'
              url={this.state.viewContentUrl} />
          );
        })() : null}
        {this.state.viewContentDocreaderShowing ? (() => {
          return (
            <DocReaderModal
              closeModalCallback={this.closeViewContent}
              contentMode={CONTENT_MODE.PREVIEW}
              contentName={this.state.viewContentName}
              isTeacher={userManager.isTeacher}
              url={this.state.viewContentUrl} />
          );
        })() : null}
        {this.state.takeAsStudentModalOpen ? (() => {
          const { TakeAsStudentModal } = this;
          const { classroomManager, t } = this.props;
          const classroomId = this.state.zeroStateClassroomId
            || classroomManager.currentClassroomId
            || this.state.currentClassroomId;
          return (
            <TakeAsStudentModal
              classroomId={classroomId}
              onClose={this.handleCloseTakeAsStudentModal}
              onConfirm={this.handleTakeAsStudentConfirm}
              open={this.state.takeAsStudentModalOpen}
              t={t} />
          );
        })() : null}
        {this.state.takeAsStudentLessonShowing ? (() => {
          const { FullscreenModal: TakeAsStudentFullscreenModal } = this;
          return (
            <TakeAsStudentFullscreenModal
              closeIframeCallback={this.handleCloseTakeAsStudentLesson}
              contentName={this.state.takeAsStudentContentName}
              isTeacher={userManager.isTeacher}
              page='lesson-player'
              url={this.state.takeAsStudentUrl} />
          );
        })() : null}
      </Container>
    );
  }

  renderGradebook = () => {
    const { isZeroState, loading } = this.state;
    const { gradebookManager, history, userManager } = this.props;

    if (isZeroState) {
      return this.renderZeroStateGradebook();
    }

    const {
      activeGradebookTable,
      activeGradebookType,
      currentAssignment,
      currentAssignmentId,
      isFileViewerOrDocReaderModalOpen
    } = gradebookManager;

    
    const isReadOnly = GradebookMainService.isGradebookReadOnly();

    const {
      BreadCrumbs, FullscreenModal, GradebookHeader, GradebookTable
    } = this;

    const { gradebookManagerLoadingFlagWhileHidingViewActive, forceRefreshAllGradebookData } = gradebookManager;

    return (
      <Container className={`gradebook-root-container grade-book-view ${activeGradebookType} ${activeGradebookTable}`} fluid>
        <div className={classNames(`gradebook-container ${activeGradebookType} ${activeGradebookTable}`, {
          'is-read-only': isReadOnly
        })}>
          <div className='gradebook-breadcrumbs-wrapper'>
            {(!loading && !gradebookManagerLoadingFlagWhileHidingViewActive && !forceRefreshAllGradebookData)
              && <BreadCrumbs history={history} />}
          </div>
          <div className={`gradebook-inner-container ${activeGradebookType} ${activeGradebookTable}`}>
            {(() => {
              // Do not show loader if FileViewer or DocReader modal is open
              const showLoader = !isFileViewerOrDocReaderModalOpen && (
                loading || gradebookManagerLoadingFlagWhileHidingViewActive || forceRefreshAllGradebookData
              );
              return showLoader ? (
                <Loader active />
              ) : (
                currentAssignmentId && (
                  <div className='grade-book-layout'>
                    <div className='right-col'>
                      <GradebookHeader
                        {...this.props}
                        // TODO remove, unused // onClickAggregateGradebookButton={this.handleClickAggregateGradebookButton}
                        assignment={currentAssignment}
                        assignmentEntityTypeId={currentAssignment.contentItemEntityTypeId || currentAssignment.entityTypeId}
                        onClickAssignButton={this.handleOpenAddAssignmentModal}
                        onClickEditBulkAssignmentButton={this.handleOpenEditBulkAssignmentModal}
                        onClickExportGradebook={this.handleExportGradebook}
                        onClickGotoCourse={this.handleGotoCourse}
                        onClickPresentAssignment={this.handlePresentAssignment}
                        onClickRecordStudentResponse={this.handleOpenTakeAsStudentModal}
                        onClickViewContent={this.handleViewContent}
                        onClickGradebookContextDetailsButton={this.refreshAllGradebookData}
                        onClickGradebookContextEngagementButton={this.handleEngagementButtonClick}
                        onClickGradebookContextLikertButton={this.refreshAllGradebookData}
                        onClickGradebookContextPlacementButton={this.refreshAllGradebookData}
                        onClickPlacementAddToClassroomButton={this.handlePlacementAddToClassroom}
                        onClickPlacementAddToGroupButton={this.handlePlacementAddToGroup}
                        onClickGradebookContextStandardsButton={this.refreshAllGradebookData}
                        onClickGradebookContextSummaryButton={this.refreshAllGradebookData}
                        onClickGradebookContextTypingButton={this.refreshAllGradebookData}
                        onClickInfoIconButton={() => this.fetchCourseActivityInformation(currentAssignment)} />
                      <GradebookTable
                        {...this.props}
                        activeGradebookTable={activeGradebookTable}
                        assignmentEntityTypeId={currentAssignment.contentItemEntityTypeId || currentAssignment.entityTypeId}
                        handleOpenEditBulkAssignmentModal={this.handleOpenEditBulkAssignmentModal}
                        isAIGradingEnabled={gradebookManager.isAIGradingEnabled}
                        onClickAssignButton={this.handleOpenAddAssignmentModal}
                        refreshAllGradebookData={this.refreshAllGradebookData} />
                    </div>
                  </div>
                )
              );
            })()}
          </div>
          {/* eslint-disable-next-line react/destructuring-assignment */}
          {this.state.showAnswers && gradebookManager.graderUrl ? (
            <FullscreenModal
              closeIframeCallback={this.hideIframe}
              contentName='Answers'
              isTeacher={userManager.isTeacher}
              page='lesson-player'
              // TODO: put here correct contentName for correct iframe title
              url={gradebookManager.graderUrl} />
          ) : null}
          {/* eslint-disable-next-line react/destructuring-assignment */}
          {this.state.viewContentLessonShowing ? (
            <FullscreenModal
              closeIframeCallback={this.closeViewContent}
              // eslint-disable-next-line react/destructuring-assignment
              contentName={this.state.viewContentName}
              isTeacher={userManager.isTeacher}
              page='lesson-player'
              // eslint-disable-next-line react/destructuring-assignment
              url={this.state.viewContentUrl} />
          ) : null}
          {/* eslint-disable-next-line react/destructuring-assignment */}
          {this.state.viewContentFileViewerShowing ? (() => {
            const { FileViewerModal } = this;
            return (
              <FileViewerModal
                closeModalCallback={this.closeViewContent}
                contentMode={CONTENT_MODE.PREVIEW}
                // eslint-disable-next-line react/destructuring-assignment
                contentName={this.state.viewContentName}
                // eslint-disable-next-line react/destructuring-assignment
                isFlowpaper={this.state.viewContentIsFlowpaper}
                isTeacher={userManager.isTeacher}
                page='file-viewer'
                // eslint-disable-next-line react/destructuring-assignment
                url={this.state.viewContentUrl} />
            );
          })() : null}
          {/* eslint-disable-next-line react/destructuring-assignment */}
          {this.state.viewContentDocreaderShowing ? (() => {
            const { DocReaderModal } = this;
            return (
              <DocReaderModal
                closeModalCallback={this.closeViewContent}
                contentMode={CONTENT_MODE.PREVIEW}
                // eslint-disable-next-line react/destructuring-assignment
                contentName={this.state.viewContentName}
                isTeacher={userManager.isTeacher}
                // eslint-disable-next-line react/destructuring-assignment
                url={this.state.viewContentUrl} />
            );
          })() : null}
        </div>
        {/* eslint-disable-next-line react/destructuring-assignment */}
        {this.state.placementAddToClassroomModalOpen ? (() => {
          const { GradebookPlacementAddToClassroomModal } = this;
          const { t } = this.props;
          return (
            <GradebookPlacementAddToClassroomModal
              onClose={this.handleClosePlacementAddToClassroomModal}
              onNext={this.handlePlacementAddToClassroomNext}
              open={this.state.placementAddToClassroomModalOpen}
              t={t} />
          );
        })() : null}
        {/* eslint-disable-next-line react/destructuring-assignment */}
        {this.state.placementAddToGroupModalOpen ? (() => {
          const { GradebookPlacementAddToGroupModal } = this;
          const { t } = this.props;
          return (
            <GradebookPlacementAddToGroupModal
              classroomId={this.state.currentClassroomId}
              onClose={this.handleClosePlacementAddToGroupModal}
              open={this.state.placementAddToGroupModalOpen}
              t={t} />
          );
        })() : null}
        {/* eslint-disable-next-line react/destructuring-assignment */}
        {this.state.takeAsStudentModalOpen ? (() => {
          const { TakeAsStudentModal } = this;
          const { gradebookManager, t } = this.props;
          const classroomId = gradebookManager.currentAssignment?.classroomId
            || this.state.currentClassroomId;
          return (
            <TakeAsStudentModal
              classroomId={classroomId}
              onClose={this.handleCloseTakeAsStudentModal}
              onConfirm={this.handleTakeAsStudentConfirm}
              open={this.state.takeAsStudentModalOpen}
              t={t} />
          );
        })() : null}
        {/* eslint-disable-next-line react/destructuring-assignment */}
        {this.state.takeAsStudentLessonShowing ? (() => {
          const { FullscreenModal } = this;
          const { userManager } = this.props;
          return (
            <FullscreenModal
              closeIframeCallback={this.handleCloseTakeAsStudentLesson}
              // eslint-disable-next-line react/destructuring-assignment
              contentName={this.state.takeAsStudentContentName}
              isTeacher={userManager.isTeacher}
              page='lesson-player'
              // eslint-disable-next-line react/destructuring-assignment
              url={this.state.takeAsStudentUrl} />
          );
        })() : null}
      </Container>
    );
  }

  render() {
    return (
      <>
        {this.renderGradebook()}
      </>
    );
  }
}

SatCoreRegister('Gradebook', Gradebook);
